import { LocalizationProvider } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button, Grid, InputAdornment, TextField, Typography } from '@material-ui/core';
import { Box } from '@mui/system';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadGridCustomer } from '../../helpers/loadGrid';
import { startUpdateClient, validarAEATClient } from '../../redux/actions/clients';
import { useFormStyles } from '../../styles/formStyles';
import { validationSchemaCustomer } from '../../utils/schemaValidationForm';
import esLocale from 'date-fns/locale/es';
import { Spinner } from '../ui/Spinner';
import { navigate } from '@reach/router';
import { getCountries } from '@/redux/actions/countryService';
import { useAppSelector } from '@/hooks/useAppSelector';
import { StandardAutocomplete } from '@/components/ui/StandardAutocomplete';
import { ApartmentOutlined } from '@material-ui/icons';
import { SwitchMUI } from '@/components/ui/Switch';
import { ForceOtroDocumentoProbatorioEffect } from '@/helpers/formik-effects/forceOtroDocumentoProbatorioEffect';
import { shouldForceOtroDocumentoProbatorio } from '@/utils/nemonIntegration';
import { syncNemonClient } from '@/redux/actions/clients';
import SyncIcon from '@mui/icons-material/Sync';

export const ClientInfo = () => {
  const classes = useFormStyles();
  const { active } = useSelector(state => state.client);
  const dispatch = useDispatch();
  const { loading } = useSelector(state => state.ui);

  const { countries } = useAppSelector(state => state.country);

  const [isLoadingSync, setIsLoadingSync] = useState(false);
  const [isLoadingValidarAEAT, setIsLoadingValidarAEAT] = useState(false);

  const countryOptions = countries.map(country => ({
    label: country.Nombre,
    value: country.PkPaises,
  }));

  useEffect(() => {
    dispatch(getCountries());
  }, [dispatch]);

  const initialValues = {
    Nombre: active?.Nombre,
    Apellidos: active?.Apellidos,
    Email: active?.Email,
    Telefono: active?.Telefono,
    NumeroIdentificacionFiscal: active?.NumeroIdentificacionFiscal,
    Observaciones: active?.Observaciones,
    OtroDocumentoProbatorio: active?.OtroDocumentoProbatorio,
    Direccion: active?.Direccion,
    Numero: active?.Numero,
    Piso: active?.Piso,
    DireccionComplementaria: active?.DireccionComplementaria,
    CodigoPostal: active?.CodigoPostal,
    Municipio: active?.Municipio,
    Provincia: active?.Provincia,
    FkPaises: active?.FkPaises,
    EsEmpresa: active?.EsEmpresa,
    IdNemon: active?.IdNemon,
    ClienteTemporal: active?.ClienteTemporal,
  };

  const { data } = loadGridCustomer();

  const handleSubmit = (values, { setSubmitting }) => {
    const client = {
      ...values,
      PkClientes: active?.PkClientes,
      VersionFila: active?.VersionFila,
    };
    dispatch(startUpdateClient(client, setSubmitting));
  };

  const handleValidarAEAT = () => {
    dispatch(validarAEATClient(active?.PkClientes, setIsLoadingValidarAEAT));
  };

  const handleSyncNemon = () => {
    dispatch(syncNemonClient(active?.PkClientes, setIsLoadingSync));
  };

  return loading ? (
    <Spinner />
  ) : (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={esLocale}>
      <Formik
        validationSchema={validationSchemaCustomer}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={handleSubmit}>
        {({ handleChange, errors, values, handleBlur, isSubmitting, touched, isValid, setFieldValue }) => {
          const selectedCountry = countries.find(c => c.PkPaises === values.FkPaises);

          const forceOtroDocumentoProbatorio = selectedCountry
            ? shouldForceOtroDocumentoProbatorio(
                selectedCountry.Codigo,
                selectedCountry?.EsIntracomunitario,
                values.EsEmpresa,
              )
            : false;

          return (
            <Grid md={8} xs={11}>
              <Form>
                <ForceOtroDocumentoProbatorioEffect countries={countries} />
                <Box sx={{ p: 2, mb: 4 }}>
                  <Typography variant="h1">Cliente</Typography>
                </Box>
                <Grid container spacing={3}>
                  <>
                    <Grid item md={3} xs={12}>
                      <SwitchMUI
                        label={(() => {
                          const selectedCountry = countries.find(c => c.PkPaises === values.FkPaises);
                          return selectedCountry?.Codigo?.toLowerCase() === 'es'
                            ? 'No censado'
                            : 'Otro documento probatorio';
                        })()}
                        name="OtroDocumentoProbatorio"
                        values={values}
                        setFieldValue={setFieldValue}
                        style={undefined}
                        disabled={forceOtroDocumentoProbatorio}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <SwitchMUI
                        label="Cliente temporal"
                        name="ClienteTemporal"
                        values={values}
                        setFieldValue={setFieldValue}
                        style={undefined}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Box display={'flex'} flexDirection="row" gap={4}>
                        <LoadingButton
                          variant="contained"
                          type="button"
                          loading={isSubmitting || isLoadingValidarAEAT}
                          disabled={!isValid || active?.OtroDocumentoProbatorio || active?.ClienteTemporal}
                          style={{
                            backgroundColor: '#E5824F',
                          }}
                          onClick={handleValidarAEAT}>
                          Validar con AEAT
                        </LoadingButton>
                        <LoadingButton
                          variant="contained"
                          type="button"
                          loading={isSubmitting || isLoadingSync}
                          disabled={!isValid || active?.ClienteTemporal}
                          style={{
                            backgroundColor: '#E5824F',
                          }}
                          startIcon={<SyncIcon />}
                          onClick={handleSyncNemon}>
                          Sincronizar
                        </LoadingButton>
                      </Box>
                    </Grid>
                  </>
                  {data.map(element => {
                    if (values.EsEmpresa && (element.name === 'Nombre' || element.name === 'Apellidos')) {
                      if (element.name === 'Apellidos') return null;
                      return (
                        <>
                          <Grid md={12} item classes={{ root: classes.container }}>
                            <TextField
                              fullWidth
                              name={'Nombre'}
                              size="small"
                              classes={{ root: classes.inputText }}
                              id={`outlined-basic-${'Nombre'}`}
                              label={'Razón social'}
                              variant="outlined"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <ApartmentOutlined />
                                  </InputAdornment>
                                ),
                                classes: {
                                  input: classes.resize,
                                },
                              }}
                              value={values?.Nombre}
                              InputLabelProps={{ style: { fontSize: '0.8em' } }}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={touched['Nombre'] && Boolean(errors['Nombre'])}
                              helperText={touched['Nombre'] && errors['Nombre']}
                              disabled={isSubmitting}
                              required
                            />
                          </Grid>
                        </>
                      );
                    }
                    if (element.name === 'FkPaises') {
                      return (
                        <Grid key={element.name} item md={6} xs={12}>
                          <StandardAutocomplete
                            name={element.name}
                            label={'País'}
                            value={values.FkPaises}
                            onChange={val => setFieldValue('FkPaises', Number(val))}
                            options={countryOptions}
                            required={!values.ClienteTemporal}
                          />
                        </Grid>
                      );
                    }
                    if (element.name === 'NumeroIdentificacionFiscal') {
                      return (
                        <>
                          <Grid xs={12} md={6} item classes={{ root: classes.container }}>
                            <TextField
                              fullWidth
                              name={element.name}
                              size="small"
                              classes={{ root: classes.inputText }}
                              id={`outlined-basic-${element.name}`}
                              label={(() => {
                                const selectedCountry = countries.find(c => c.PkPaises === values.FkPaises);
                                if (values.OtroDocumentoProbatorio) return 'ID';
                                if (active?.EsEmpresa) {
                                  if (selectedCountry?.Codigo?.toLowerCase() === 'es') return 'CIF';
                                  if (selectedCountry?.EsIntracomunitario) return 'VAT Number';
                                  return 'ID';
                                }
                                return selectedCountry?.Codigo?.toLowerCase() === 'es' ? 'NIF' : 'ID';
                              })()}
                              variant="outlined"
                              value={values?.NumeroIdentificacionFiscal}
                              InputProps={{
                                startAdornment: values.EsEmpresa ? (
                                  <InputAdornment position="start">
                                    <ApartmentOutlined />
                                  </InputAdornment>
                                ) : null,
                                classes: {
                                  input: classes.resize,
                                },
                              }}
                              InputLabelProps={{ style: { fontSize: '0.8em' } }}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={touched[element.name] && Boolean(errors[element.name])}
                              helperText={touched[element.name] && errors[element.name]}
                              disabled={isSubmitting}
                              required={!values.ClienteTemporal}
                            />
                          </Grid>
                          {!values.ClienteTemporal && (
                            <>
                              <Grid item md={6} xs={12}>
                                <TextField
                                  fullWidth
                                  name="IdNemon"
                                  size="small"
                                  classes={{ root: classes.inputText }}
                                  id={`outlined-basic-IdNemon`}
                                  label="Id Nemon"
                                  variant="outlined"
                                  InputProps={{
                                    classes: {
                                      input: classes.resize,
                                    },
                                  }}
                                  InputLabelProps={{ style: { fontSize: '0.8em' } }}
                                  value={values.IdNemon ? values.IdNemon : 'Cliente no sincronizado con Nemon'}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  error={touched.IdNemon && Boolean(errors.IdNemon)}
                                  helperText={touched.IdNemon && errors.IdNemon}
                                  disabled={true}
                                />
                              </Grid>
                            </>
                          )}
                        </>
                      );
                    }
                    return (
                      <Grid key={element.name} item md={6} xs={12}>
                        <TextField
                          fullWidth
                          name={element.name}
                          size="small"
                          classes={{ root: classes.inputText }}
                          id={`outlined-basic-${element.name}`}
                          label={element.label}
                          variant="outlined"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">{element.startAdornment}</InputAdornment>,
                            classes: {
                              input: classes.resize,
                            },
                          }}
                          InputLabelProps={{ style: { fontSize: '0.8em' } }}
                          value={values[element.name]}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={touched[element.name] && Boolean(errors[element.name])}
                          helperText={touched[element.name] && errors[element.name]}
                          disabled={isSubmitting}
                          required={
                            !values.ClienteTemporal
                              ? [
                                  'Nombre',
                                  'Telefono',
                                  'Apellidos',
                                  'NumeroIdentificacionFiscal',
                                  'Direccion',
                                  'CodigoPostal',
                                  'FkPaises',
                                  'Municipio',
                                  'Provincia',
                                ].includes(element.name)
                              : ['Nombre', 'Telefono', 'Apellidos'].includes(element.name)
                          }
                          multiline={element.name === 'Observaciones' ? true : false}
                          maxRows={element.name === 'Observaciones' ? 5 : undefined}
                          minRows={element.name === 'Observaciones' ? 5 : undefined}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    p: 2,
                  }}>
                  <Button
                    style={{
                      marginRight: '20px',
                      backgroundColor: '#F39868',
                      color: '#fff',
                      borderRadius: 7,
                      marginLeft: 10,
                    }}
                    onClick={() => navigate('/clients')}>
                    <Typography style={{ fontWeight: 'bold' }}>Cancelar </Typography>
                  </Button>

                  <LoadingButton
                    variant="contained"
                    type="submit"
                    loading={isSubmitting}
                    disabled={!isValid}
                    style={{
                      backgroundColor: '#E5824F',
                    }}>
                    Actualizar
                  </LoadingButton>
                </Box>
              </Form>
            </Grid>
          );
        }}
      </Formik>
    </LocalizationProvider>
  );
};
